import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');

  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsSubmitting(true);

      try {
        const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, ...(mode === 'register' ? { name: email.split('@')[0] } : {}) }),
        });

        const data = await res.json();

        if (!data.success) {
          setError(data.error ?? 'Wystąpił błąd');
          setIsSubmitting(false);
          return;
        }

        login(data.data.user, data.data.accessToken, data.data.refreshToken);
        navigate('/', { replace: true });
      } catch {
        setError('Nie można połączyć z serwerem');
        setIsSubmitting(false);
      }
    },
    [email, password, mode, login, navigate],
  );

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-2 text-center text-2xl font-bold text-primary-600">
          Bransoletki Designer
        </h1>
        <p className="mb-8 text-center text-sm text-gray-500">
          {mode === 'login' ? 'Zaloguj się do swojego konta' : 'Utwórz nowe konto'}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="selectable rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Hasło"
            required
            minLength={mode === 'register' ? 8 : 1}
            className="selectable rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />

          {error && <p className="text-sm text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Proszę czekać...' : mode === 'login' ? 'Zaloguj' : 'Zarejestruj'}
          </button>
        </form>

        <div className="mt-6 text-center">
          {mode === 'login' ? (
            <p className="text-sm text-gray-500">
              Nie masz konta?{' '}
              <button onClick={() => setMode('register')} className="font-medium text-primary-600">
                Zarejestruj się
              </button>
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              Masz już konto?{' '}
              <button onClick={() => setMode('login')} className="font-medium text-primary-600">
                Zaloguj się
              </button>
            </p>
          )}
        </div>

        <Link to="/landing" className="mt-4 block text-center text-xs text-gray-400 hover:text-gray-600">
          ← Wróć do strony głównej
        </Link>
      </div>
    </div>
  );
}
